import httpClient from "@utils/httpClient";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Reporting Manager Relationship
 */
export interface ReportingManagerRelationship {
  id: number;
  user_id: number;
  reporting_manager_id: number;
  user_name: string;
  user_email: string;
  manager_name: string;
  manager_email: string;
  created_at: string;
}

/**
 * Manager for a user
 */
export interface Manager {
  reporting_manager_id: number;
  manager_name: string;
  manager_email: string;
}

/**
 * Reportee for a manager
 */
export interface Reportee {
  reportee_id: number;
  reportee_name: string;
  reportee_email: string;
}

/**
 * Token information
 */
export interface FeedbackToken {
  token_id: number;
  token: string;
  respondent_user_id: number;
  respondent_name: string;
  respondent_email: string;
  respondent_type: 'manager' | 'reportee';
  is_used: boolean;
  submitted_at: string | null;
}

/**
 * 360 Feedback Assessment
 */
export interface Feedback360Assessment {
  config_id: number;
  assessment_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  title: string;
  status: 'pending' | 'self_completed' | 'waiting_responses' | 'completed';
  self_submitted: boolean;
  self_submitted_at: string | null;
  created_at: string;
  expires_at: string;
  total_respondents: number;
  completed_respondents: number;
}

/**
 * Competency Area
 */
export interface CompetencyArea {
  competency_id: number;
  competency_name: string;
  competency_description: string;
  competency_order: number;
  questions: FeedbackQuestion[];
}

/**
 * Feedback Question
 */
export interface FeedbackQuestion {
  question_id: number;
  question_text: string;
  question_order: number;
}

/**
 * Create 360 Feedback Data
 */
export interface Create360FeedbackData {
  user_id: number;
  title?: string;
  expires_at?: string;
}

/**
 * Create Relationship Data
 */
export interface CreateRelationshipData {
  user_id: number;
  reporting_manager_id: number;
  // Optional fields for manual manager entry
  manager_name?: string;
  manager_email?: string;
  // Optional fields for manual employee entry (bulk upload)
  employee_name?: string;
  employee_email?: string;
}

/**
 * Create Bulk Relationships Data (for existing users)
 */
export interface CreateBulkRelationshipsData {
  user_id: number;
  manager_ids: number[];
}

/**
 * Bulk Upload Relationship Data (CSV upload)
 */
export interface BulkUploadRelationshipData {
  employee_name: string;
  employee_email: string;
  manager_name: string;
  manager_email: string;
}

/**
 * Bulk Upload Request Data
 */
export interface BulkUploadRequest {
  relationships: BulkUploadRelationshipData[];
  assessment_id?: number;
  group_id?: number;
}

/**
 * Reportee Relationship
 */
export interface ReporteeRelationship {
  id: number;
  user_id: number;
  reportee_id: number | null;
  user_name: string;
  user_email: string;
  reportee_name: string;
  reportee_email: string;
  is_external_reportee: boolean;
  created_at: string;
}

/**
 * Create Reportee Relationship Data
 */
export interface CreateReporteeRelationshipData {
  user_id: number;
  reportee_id?: number;
  reportee_name?: string;
  reportee_email?: string;
}

/**
 * Create Bulk Reportee Relationships Data (for existing users)
 */
export interface CreateBulkReporteeRelationshipsData {
  user_id: number;
  reportee_ids: number[];
}

/**
 * Bulk Upload Reportee Relationship Data (CSV upload)
 */
export interface BulkUploadReporteeRelationshipData {
  employee_name: string;
  employee_email: string;
  reportee_name: string;
  reportee_email: string;
}

/**
 * Bulk Upload Reportee Request Data
 */
export interface BulkUploadReporteeRequest {
  relationships: BulkUploadReporteeRelationshipData[];
  assessment_id?: number;
  group_id?: number;
}

/**
 * API Response wrapper
 */
interface ApiResponse<T = any> {
  success: boolean;
  data: T | null;
  error: { message: string } | null;
}

// ============================================================================
// REPORTING MANAGER RELATIONSHIP METHODS
// ============================================================================

/**
 * Get all reporting manager relationships
 * @returns {Promise<ApiResponse<ReportingManagerRelationship[]>>} List of all relationships
 */
export const getAllReportingManagers = async (): Promise<ApiResponse<ReportingManagerRelationship[]>> => {
  try {
    const response = await httpClient.get('/reporting-managers/all');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getAllReportingManagers error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch reporting managers' }
    };
  }
};

/**
 * Get managers for a specific user
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<Manager[]>>} List of managers
 */
export const getManagersForUser = async (userId: number): Promise<ApiResponse<Manager[]>> => {
  try {
    const response = await httpClient.get(`/reporting-managers/user/${userId}`);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getManagersForUser error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch managers' }
    };
  }
};

/**
 * Get reportees for a specific manager
 * @param {number} managerId - Manager ID
 * @returns {Promise<ApiResponse<Reportee[]>>} List of reportees
 */
export const getReporteesForManager = async (managerId: number): Promise<ApiResponse<Reportee[]>> => {
  try {
    const response = await httpClient.get(`/reporting-managers/reportees/${managerId}`);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getReporteesForManager error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch reportees' }
    };
  }
};

/**
 * Create a reporting manager relationship
 * @param {CreateRelationshipData} relationshipData - Relationship data
 * @returns {Promise<ApiResponse<any>>} Create response
 */
export const createReportingManager = async (relationshipData: CreateRelationshipData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reporting-managers', relationshipData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createReportingManager error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create relationship' }
    };
  }
};

/**
 * Create bulk reporting manager relationships
 * @param {CreateBulkRelationshipsData} bulkData - Bulk relationship data
 * @returns {Promise<ApiResponse<any>>} Create response
 */
export const createBulkReportingManagers = async (bulkData: CreateBulkRelationshipsData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reporting-managers/bulk', bulkData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createBulkReportingManagers error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create relationships' }
    };
  }
};

/**
 * Bulk upload reporting manager relationships from CSV
 * @param {BulkUploadRequest} uploadData - Bulk upload data with employee and manager details
 * @returns {Promise<ApiResponse<any>>} Upload response
 * @description
 * This endpoint handles bulk upload of relationships where:
 * - Employees are looked up by email, created if they don't exist
 * - Managers are looked up by email, can be external (not in users table)
 * - Relationships are created with manager name and email stored
 */
export const bulkUploadReportingManagers = async (uploadData: BulkUploadRequest): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reporting-managers/bulk-upload', uploadData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('bulkUploadReportingManagers error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to upload relationships' }
    };
  }
};

/**
 * Delete a reporting manager relationship
 * @param {number} userId - User ID
 * @param {number} managerId - Manager ID (optional if managerEmail is provided)
 * @param {string} managerEmail - Manager Email (optional if managerId is provided)
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteReportingManager = async (
  userId: number,
  managerId?: number | null,
  managerEmail?: string | null
): Promise<ApiResponse<any>> => {
  try {
    const deleteData: any = { user_id: userId };

    // Add either managerId or managerEmail
    if (managerId) {
      deleteData.reporting_manager_id = managerId;
    }
    if (managerEmail) {
      deleteData.manager_email = managerEmail;
    }

    const response = await httpClient.delete('/reporting-managers', {
      data: deleteData
    });

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteReportingManager error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete relationship' }
    };
  }
};

// ============================================================================
// REPORTEE RELATIONSHIP METHODS
// ============================================================================

/**
 * Get all reportee relationships
 * @returns {Promise<ApiResponse<ReporteeRelationship[]>>} List of all reportee relationships
 */
export const getAllReportees = async (): Promise<ApiResponse<ReporteeRelationship[]>> => {
  try {
    const response = await httpClient.get('/reportees/all');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getAllReportees error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch reportees' }
    };
  }
};

/**
 * Get reportees for a specific user
 * @param {number} userId - User ID
 * @returns {Promise<ApiResponse<Reportee[]>>} List of reportees
 */
export const getReporteesForUser = async (userId: number): Promise<ApiResponse<Reportee[]>> => {
  try {
    const response = await httpClient.get(`/reportees/user/${userId}`);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getReporteesForUser error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch reportees' }
    };
  }
};

/**
 * Create a reportee relationship
 * @param {CreateReporteeRelationshipData} relationshipData - Relationship data
 * @returns {Promise<ApiResponse<any>>} Create response
 */
export const createReportee = async (relationshipData: CreateReporteeRelationshipData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reportees', relationshipData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createReportee error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create reportee relationship' }
    };
  }
};

/**
 * Create bulk reportee relationships
 * @param {CreateBulkReporteeRelationshipsData} bulkData - Bulk relationship data
 * @returns {Promise<ApiResponse<any>>} Create response
 */
export const createBulkReportees = async (bulkData: CreateBulkReporteeRelationshipsData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reportees/bulk', bulkData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('createBulkReportees error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create bulk reportee relationships' }
    };
  }
};

/**
 * Bulk upload reportee relationships from CSV
 * @param {BulkUploadReporteeRequest} uploadData - Bulk upload data with employee and reportee details
 * @returns {Promise<ApiResponse<any>>} Upload response
 * @description
 * This endpoint handles bulk upload of reportee relationships where:
 * - Employees are looked up by email, created if they don't exist
 * - Reportees are looked up by email, can be external (not in users table)
 * - Relationships are created with reportee name and email stored
 */
export const bulkUploadReportees = async (uploadData: BulkUploadReporteeRequest): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/reportees/bulk-upload', uploadData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('bulkUploadReportees error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to upload reportee relationships' }
    };
  }
};

/**
 * Delete a reportee relationship
 * @param {number} userId - User ID
 * @param {number} reporteeId - Reportee ID (optional if reporteeEmail is provided)
 * @param {string} reporteeEmail - Reportee Email (optional if reporteeId is provided)
 * @returns {Promise<ApiResponse<any>>} Delete response
 */
export const deleteReportee = async (
  userId: number,
  reporteeId?: number | null,
  reporteeEmail?: string | null
): Promise<ApiResponse<any>> => {
  try {
    const deleteData: any = { user_id: userId };

    // Add either reporteeId or reporteeEmail
    if (reporteeId) {
      deleteData.reportee_id = reporteeId;
    }
    if (reporteeEmail) {
      deleteData.reportee_email = reporteeEmail;
    }

    const response = await httpClient.delete('/reportees', {
      data: deleteData
    });

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('deleteReportee error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete reportee relationship' }
    };
  }
};

// ============================================================================
// 360 FEEDBACK ASSESSMENT METHODS
// ============================================================================

/**
 * Create a new 360 feedback assessment
 * @param {Create360FeedbackData} feedbackData - Feedback assessment data
 * @returns {Promise<ApiResponse<any>>} Create response
 */
export const create360Feedback = async (feedbackData: Create360FeedbackData): Promise<ApiResponse<any>> => {
  try {
    const response = await httpClient.post('/360-feedback/create', feedbackData);

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    console.error('create360Feedback error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create 360 feedback assessment' }
    };
  }
};

/**
 * Get all 360 feedback assessments
 * @returns {Promise<ApiResponse<Feedback360Assessment[]>>} List of assessments
 */
export const getAll360Feedback = async (): Promise<ApiResponse<Feedback360Assessment[]>> => {
  try {
    const response = await httpClient.get('/360-feedback/list');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getAll360Feedback error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch 360 feedback assessments' }
    };
  }
};

/**
 * Get all competencies and questions for 360 feedback
 * @returns {Promise<ApiResponse<CompetencyArea[]>>} List of competencies with questions
 */
export const get360Competencies = async (): Promise<ApiResponse<CompetencyArea[]>> => {
  try {
    const response = await httpClient.get('/360-feedback/competencies');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('get360Competencies error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch competencies' }
    };
  }
};

// ============================================================================
// 360 FEEDBACK RESULTS & CONSOLIDATED REPORT METHODS
// ============================================================================

/**
 * Participant Result for consolidated report
 */
export interface ParticipantResult {
  userId: number;
  userName: string;
  userEmail: string;
  sessionId: number;
  sessionStatus: string;
  participantSubmittedAt: string;
  managerResponseCount: number;
  reporteeResponseCount: number;
  totalExpectedResponses: number;
}

/**
 * Consolidated PDF Request Data
 */
export interface ConsolidatedPDFRequest {
  userIds: number[];
}

/**
 * Get 360 feedback results/participants for an assessment (only those who have attempted)
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<ParticipantResult[]>>} List of participants with results
 */
export const getFeedback360Results = async (assessmentId: number): Promise<ApiResponse<ParticipantResult[]>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}/feedback-360-results`);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getFeedback360Results error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch feedback 360 results' }
    };
  }
};

/**
 * Get all users for consolidated report selection (includes users who haven't attempted)
 * @param {number} assessmentId - Assessment ID
 * @returns {Promise<ApiResponse<ParticipantResult[]>>} List of ALL users (not just participants)
 */
export const getConsolidatedParticipants = async (assessmentId: number): Promise<ApiResponse<ParticipantResult[]>> => {
  try {
    const response = await httpClient.get(`/assessments/${assessmentId}/feedback-360/consolidated-participants`);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('getConsolidatedParticipants error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch participants' }
    };
  }
};

/**
 * Download consolidated PDF for selected users
 * @param {number} assessmentId - Assessment ID
 * @param {ConsolidatedPDFRequest} requestData - Request data with userIds and selectionType
 * @returns {Promise<ApiResponse<Blob>>} PDF blob
 */
export const downloadConsolidatedPDF = async (
  assessmentId: number,
  requestData: ConsolidatedPDFRequest
): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/assessments/${assessmentId}/feedback-360/consolidated-result`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to download consolidated result');
    }

    const blob = await response.blob();

    return {
      success: true,
      data: blob,
      error: null
    };
  } catch (error: any) {
    console.error('downloadConsolidatedPDF error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to download consolidated PDF' }
    };
  }
};
